import { useState } from 'react';
import { Button, Drawer, Input, Modal, Segmented, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { CARGO_LABEL, cai, fmtM, timeOf, whOf, type MapRoute, type TollStation } from './mapRoutes';
import { whRestrict } from './places';
import { parseXY } from './placeForm';
import s from './places.module.css';
import './i18n';

// Form Thêm / Sửa tuyến (Mint-TMS-Places drawers.route) + Thêm / Sửa trạm (modals.toll). Chỉ giả lập: Lưu / Xoá báo toast, không ghi DB.
// Dòng tiền (Giá nhà xe theo tuyến, Giá L4, Vé tháng) ẩn: màn chưa có quyền money (board money=hide).
const CARGOS = ['RF', 'DC'] as const;
const SHAPES = ['Vòng về cảng cũ', 'Một chiều', 'Khứ hồi'] as const;
const SYSTEMS = ['Thu kín', 'Thu mở'] as const;

export function RouteForm({ open, onClose, route, approved }: { open: boolean; onClose: () => void; route: MapRoute | null; approved: boolean }) {
  const t = useT();
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>({});
  const [cargo, setCargo] = useState<(typeof CARGOS)[number] | null>(null);
  const [shape, setShape] = useState<(typeof SHAPES)[number]>('Vòng về cảng cũ');
  const [err, setErr] = useState(false);
  const w = route ? whOf(route.wh) : undefined;
  const get = (k: string, d: string) => (v[k] !== undefined ? v[k] : d);
  const set = (k: string) => (e: { target: { value: string } }) => setV((o) => ({ ...o, [k]: e.target.value }));
  const whDef = route ? route.wh + (w ? ` · ${w.name}` : '') : '';
  const wh = get('wh', whDef);
  const curCargo = cargo ?? route?.cargo ?? null;
  const tm = route ? timeOf(route) : null;
  const wr = w ? whRestrict(w) : null;
  const ok = !!route && (route.ok || approved);

  const close = () => { setV({}); setCargo(null); setShape('Vòng về cảng cũ'); setErr(false); onClose(); };
  const save = () => {
    if (!wh.trim()) { setErr(true); return; }
    toast({ msg: t('rtform.saved'), tone: 'ok' });
    close();
  };
  const title = route ? `${route.id} · ${cai().name} → ${w?.name ?? route.wh}` : t('rtform.add');
  const sub = route ? `${CARGO_LABEL[route.cargo]} · ${route.tier} · ${route.trips} chuyến` : t('rtform.subNew');

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={title}
      description={`${route ? `${ok ? t('rtform.approved') : 'NeedVerify'} · ` : ''}${sub}`}
      footer={
        <>
          <span className={s.dim}>{route ? t('rtform.footRun', { n: route.trips }) : t('rtform.footNew')}</span>
          {route ? <Button variant="danger" onClick={() => { toast({ msg: t('rtform.deleted', { id: route.id }) }); close(); }}>{t('rtform.delete')}</Button> : <Button variant="ghost" onClick={close}>{t('plform.cancel')}</Button>}
          <Button variant="primary" onClick={save}>{t('plform.save')}</Button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); save(); }} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h4>{t('rtform.route')}</h4>
        <Segmented kind="toggle" label={t('rtform.cargo')} items={CARGOS.map((k) => ({ key: k, label: k }))} value={(curCargo ?? '') as (typeof CARGOS)[number]} onChange={setCargo} />
        <Input label={t('rtform.dp')} placeholder="CAT LAI TMNL, VN" value={get('dp', cai().name)} onChange={set('dp')} />
        <Input label={t('rtform.wh')} required placeholder="WH0021 · Kho …" value={wh} onChange={set('wh')} error={err && !wh.trim() ? t('rtform.whErr') : undefined} />
        <Segmented kind="toggle" label={t('rtform.shape')} items={SHAPES.map((k) => ({ key: k, label: k }))} value={shape} onChange={setShape} />
        <Input label="Km" value={get('km', route ? String(route.km).replace('.', ',') : '')} onChange={set('km')} hint={t('rtform.kmHint')} />
        <h4>{t('rtform.time')}</h4>
        <div className={s.dim}>{t('rtform.timeNote')}</div>
        <dl className={s.kv}>
          {tm?.gps ? (
            <>
              <dt>{t('rtform.avg')}</dt><dd>{`${tm.k} · ${tm.n} chuyến GPS`}</dd>
              <dt>{t('rtform.range')}</dt><dd>{`${fmtM(tm.min)} – ${fmtM(tm.max)}`}</dd>
              <dt>{t('rtform.upd')}</dt><dd>{tm.upd}</dd>
            </>
          ) : (
            <>
              <dt>{t('rtform.est')}</dt><dd>{tm ? tm.k : t('rtform.afterWh')}</dd>
              <dt>{t('rtform.toGps')}</dt><dd>{t('rtform.firstTrip')}</dd>
            </>
          )}
        </dl>
        {wr && route && (
          <>
            <h4>{t('rtform.restrict')}</h4>
            <dl className={s.kv}><dt>{route.wh}</dt><dd>{[...wr.ban, wr.ton ? `≤ ${wr.ton} tấn` : '', ...wr.veh, wr.road].filter(Boolean).join(' · ')}</dd></dl>
          </>
        )}
        <h4>{t('rtform.tolls')}</h4>
        {route && route.tolls.length ? (
          <dl className={s.kv}>{route.tolls.map(([k, he, qq]) => <><dt key={`${k}-k`}>{k}</dt><dd key={`${k}-v`}>{`${he === 'KIN' ? 'Thu kín' : 'Thu mở'} · ×${qq}`}</dd></>)}</dl>
        ) : <div className={s.dim}>{t('tms.noToll')}</div>}
        <Button variant="secondary" onClick={() => toast({ msg: t('rtform.scanned', { n: route?.tolls.length ?? 0 }) })}>{t('rtform.rescan')}</Button>
      </form>
    </Drawer>
  );
}

export function TollForm({ open, onClose, toll }: { open: boolean; onClose: () => void; toll: TollStation | null }) {
  const t = useT();
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>({});
  const [sys, setSys] = useState<(typeof SYSTEMS)[number] | null>(null);
  const [err, setErr] = useState(false);
  const get = (k: string, d: string) => (v[k] !== undefined ? v[k] : d);
  const set = (k: string) => (e: { target: { value: string } }) => setV((o) => ({ ...o, [k]: e.target.value }));
  const name = get('name', toll?.name ?? '');
  const xy = get('xy', toll ? `${toll.lat}, ${toll.lng}` : '');
  const xyBad = xy.trim() !== '' && !parseXY(xy);
  const curSys = sys ?? (toll ? (toll.sys === 'KIN' ? 'Thu kín' : 'Thu mở') : null);

  const close = () => { setV({}); setSys(null); setErr(false); onClose(); };
  const save = () => {
    if (!name.trim()) { setErr(true); return; }
    if (xyBad) return;
    toast({ msg: t('tlform.saved', { name }), tone: 'ok' });
    close();
  };
  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={t('tlform.title')}
      description={t('tlform.desc')}
      footer={
        <>
          <Button variant="ghost" onClick={close}>{t('plform.cancel')}</Button>
          <Button variant="primary" onClick={save}>{t('plform.save')}</Button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); save(); }} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label={t('tlform.name')} required placeholder="Long Phước" value={name} onChange={set('name')} error={err && !name.trim() ? t('tlform.nameErr') : undefined} />
        <Segmented kind="toggle" label={t('tlform.sys')} items={SYSTEMS.map((k) => ({ key: k, label: k }))} value={(curSys ?? '') as (typeof SYSTEMS)[number]} onChange={setSys} />
        <Input label={t('tlform.corr')} placeholder="CORR02" value={get('corr', toll && toll.corridor !== '—' ? toll.corridor : '')} onChange={set('corr')} />
        <Input label={t('tlform.xy')} placeholder="10.80, 106.86" value={xy} onChange={set('xy')} error={xyBad ? t('tlform.xyBad') : undefined} />
      </form>
    </Modal>
  );
}
